import { Button, Select } from "@light-technology/components";
import { Play } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { api } from "../../state/api";
import {
  FlowScope,
  type AppResponse,
  type Enrollment,
} from "../../state/api.types";

const FlowScopeToString = {
  [FlowScope.Enrollment]: "Enrollment",
  [FlowScope.UpdatePaymentMethod]: "Update payment method",
  [FlowScope.Documents]: "Documents",
  [FlowScope.Billing]: "Billing",
  [FlowScope.Renewal]: "Renewal",
  [FlowScope.ConnectBattery]: "Connect battery",
} as const satisfies Record<FlowScope, string>;

type Item = {
  id: string;
  label: string;
};

type SandboxPreviewProps = {
  app: AppResponse;
  enrollment: Enrollment;
  onClose: () => void;
};

// Allow rewriting the URL source from what the backend provides
function processIframeSrc(url: string) {
  const rewrite = import.meta.env.VITE_EMBEDDED_URL_REWRITE;
  if (!rewrite) return url;
  const sourceUrl = new URL(url);
  const rewriteUrl = new URL(rewrite);
  rewriteUrl.pathname = sourceUrl.pathname;
  rewriteUrl.search = sourceUrl.search;
  rewriteUrl.hash = sourceUrl.hash;
  return rewriteUrl.toString();
}

export const SandboxPreview = (props: SandboxPreviewProps) => {
  const { onClose } = props;

  const getAppPlans = api.useGetAppPlansQuery(props.app.uuid);
  const [createEmbeddedAppTestLink, createEmbeddedAppTestLinkResult] =
    api.useCreateEmbeddedAppTestLinkMutation();

  // Derive items from enrollment state and plan data
  let items: Item[];
  if (props.enrollment.is_service_active) {
    items = [
      {
        id: FlowScope.UpdatePaymentMethod,
        label: FlowScopeToString[FlowScope.UpdatePaymentMethod],
      },
      {
        id: FlowScope.Documents,
        label: FlowScopeToString[FlowScope.Documents],
      },
      {
        id: FlowScope.Billing,
        label: FlowScopeToString[FlowScope.Billing],
      },
      {
        id: FlowScope.Renewal,
        label: FlowScopeToString[FlowScope.Renewal],
      },
    ];
  } else {
    items = (getAppPlans.data?.data ?? []).map((p) => ({
      id: p.uuid,
      label: FlowScopeToString[FlowScope.Enrollment] + " - " + p.name,
    }));
  }

  // Default value derived from enrollment and items
  const defaultValue: string | null = props.enrollment.is_service_active
    ? FlowScope.UpdatePaymentMethod
    : props.enrollment.is_enrollment_finalized
      ? null
      : (items[0]?.id ?? null);

  // User-controlled selection, reset when enrollment or plans change
  const [value, setValue] = useState<string | null>(defaultValue);
  const [trackedEnrollment, setTrackedEnrollment] = useState(props.enrollment);
  const [trackedPlansData, setTrackedPlansData] = useState(getAppPlans.data);

  if (
    trackedEnrollment !== props.enrollment ||
    trackedPlansData !== getAppPlans.data
  ) {
    setTrackedEnrollment(props.enrollment);
    setTrackedPlansData(getAppPlans.data);
    setValue(defaultValue);
  }

  useEffect(() => {
    const handleMessage = (event: MessageEvent<{ type?: string }>) => {
      if (event.data.type === "light-flow-close") {
        createEmbeddedAppTestLinkResult.reset();
        onClose();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [createEmbeddedAppTestLinkResult, onClose]);

  return (
    <div className="flex items-center gap-md">
      <Select
        className="flex-auto"
        items={items}
        value={value}
        onChange={(v) => {
          setValue(v as string | null);
        }}
        placeholder="None"
        aria-label="Select embedded scope"
      >
        {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
      </Select>
      <Button
        className="flex-none"
        color="primary"
        size="md"
        iconLeading={Play}
        isDisabled={!value}
        isLoading={createEmbeddedAppTestLinkResult.isLoading}
        onClick={() => {
          if (Object.values(FlowScope).includes(value as FlowScope)) {
            createEmbeddedAppTestLink({
              appUUID: props.app.uuid,
              scope: value as FlowScope,
              plan: null,
            });
          } else {
            createEmbeddedAppTestLink({
              appUUID: props.app.uuid,
              scope: FlowScope.Enrollment,
              plan: value!,
            });
          }
        }}
      />
      {createEmbeddedAppTestLinkResult.data && (
        <div className="fixed inset-0 z-9999">
          <iframe
            className="size-full border-none"
            src={processIframeSrc(
              createEmbeddedAppTestLinkResult.data.test_link,
            )}
            allow="payment"
          />
        </div>
      )}
    </div>
  );
};
